import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild, Button, Field, Label, Input, Select, Textarea } from '@headlessui/react';
import { Fragment } from 'react';
import { 
  FaTimes, FaTicketAlt, FaUser, FaClock, FaCheckCircle, FaExclamationTriangle, 
  FaTrash, FaTag, FaProjectDiagram, FaHeadset,
  FaApple, FaAndroid, FaGlobe
} from 'react-icons/fa';

const SupportTicketDetail = ({ 
  isOpen, 
  onClose, 
  supportTicket, 
  onUpdate, 
  onDelete, 
  isLoading = false 
}) => {
  const [editData, setEditData] = useState({});
  const [savingField, setSavingField] = useState(null);

  useEffect(() => {
    if (supportTicket) {
      setEditData({
        status: supportTicket.status,
        priority: supportTicket.priority
      });
    }
  }, [supportTicket]);

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-red-400 bg-red-900/20 border-red-500/30',
      in_progress: 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30',
      waiting_for_customer: 'text-blue-400 bg-blue-900/20 border-blue-500/30',
      resolved: 'text-green-400 bg-green-900/20 border-green-500/30',
      closed: 'text-gray-400 bg-gray-900/20 border-gray-500/30'
    };
    return colors[status] || 'text-gray-400 bg-gray-900/20 border-gray-500/30';
  };

  const getStatusText = (status) => {
    const texts = {
      open: 'Open',
      in_progress: 'In Progress',
      waiting_for_customer: 'Waiting for Customer',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return texts[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-400',
      medium: 'text-yellow-400',
      high: 'text-orange-400',
      urgent: 'text-red-400'
    };
    return colors[priority] || 'text-gray-400';
  };

  const getPriorityText = (priority) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'urgent') return <FaExclamationTriangle className="h-4 w-4" />;
    if (priority === 'high') return <FaExclamationTriangle className="h-4 w-4" />;
    return <FaTicketAlt className="h-4 w-4" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return formatDate(dateString);
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'ios':
        return <FaApple className="w-4 h-4 text-gray-300" />;
      case 'android':
        return <FaAndroid className="w-4 h-4 text-green-400" />;
      case 'web':
        return <FaGlobe className="w-4 h-4 text-blue-400" />;
      default:
        return <FaGlobe className="w-4 h-4 text-gray-400" />;
    }
  };

  const handleFieldChange = async (field, value) => {
    const nextData = { ...editData, [field]: value };
    setEditData(nextData);

    if (!onUpdate || value === supportTicket[field]) return;

    setSavingField(field);
    try {
      await onUpdate(supportTicket._id, { [field]: value });
    } finally {
      setSavingField(null);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(supportTicket._id);
    }
  };

  if (!supportTicket) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gray-900 p-6 text-left align-middle shadow-xl transition-all border border-gray-700">
                <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-3">
                    <FaTicketAlt className="h-6 w-6 text-indigo-400" />
                    <span>Support Ticket Details</span>
                  </div>
                  <button onClick={onClose} className="text-gray-400 hover:text-white">
                    <FaTimes />
                  </button>
                </DialogTitle>
                
                <div className="space-y-6">
                  {/* Header Info */}
                  <div className="bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-mono text-gray-400">{supportTicket.ticketNumber}</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-400">Status:</span>
                              <Select
                                value={editData.status}
                                onChange={(e) => handleFieldChange('status', e.target.value)}
                                disabled={savingField === 'status'}
                                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              >
                                <option value="open">Open</option>
                                <option value="in_progress">In Progress</option>
                                <option value="waiting_for_customer">Waiting for Customer</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                              </Select>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-400">Priority:</span>
                              <Select
                                value={editData.priority}
                                onChange={(e) => handleFieldChange('priority', e.target.value)}
                                disabled={savingField === 'priority'}
                                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              >
                                <option value="low">⚪ Low</option>
                                <option value="medium">🟡 Medium</option>
                                <option value="high">🟠 High</option>
                                <option value="urgent">🔴 Urgent</option>
                              </Select>
                            </div>
                          </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleDelete}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Delete Ticket"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <h2 className="text-xl font-semibold text-white mb-2">{supportTicket.subject}</h2>
                    <p className="text-gray-300">{supportTicket.description}</p>
                  </div>

                  {/* Ticket Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Customer Info */}
                    <div className="bg-gray-800 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-white mb-3 flex items-center">
                        <FaUser className="h-4 w-4 mr-2" />
                        Customer Information
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-400">Name:</span>
                          <p className="text-sm text-white">{supportTicket.customerInfo?.name || 'Not provided'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Email:</span>
                          <p className="text-sm text-white">{supportTicket.customerInfo?.email || 'Not provided'}</p>
                        </div>
                        {supportTicket.customerInfo?.phone && (
                          <div>
                            <span className="text-xs text-gray-400">Phone:</span>
                            <p className="text-sm text-white">{supportTicket.customerInfo.phone}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Technical Info */}
                    <div className="bg-gray-800 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-white mb-3 flex items-center">
                        <FaProjectDiagram className="h-4 w-4 mr-2" />
                        Technical Information
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          {getPlatformIcon(supportTicket.productId?.platform || supportTicket.platform)}
                          <span className="text-xs text-gray-400">Platform:</span>
                          <span className="text-sm text-white capitalize">{supportTicket.productId?.platform || supportTicket.platform || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Language:</span>
                          <p className="text-sm text-white uppercase">{supportTicket.language || 'EN'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Product:</span>
                          <p className="text-sm text-white">{supportTicket.productId?.name || 'Unknown Product'}</p>
                        </div>
                        {supportTicket.customData && Object.keys(supportTicket.customData).length > 0 && (
                          <div>
                            <span className="text-xs text-gray-400">Custom Data:</span>
                            <div className="mt-1 p-2 bg-gray-700 rounded text-xs text-gray-300">
                              <pre className="whitespace-pre-wrap">{JSON.stringify(supportTicket.customData, null, 2)}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Module Info */}
                    <div className="bg-gray-800 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-white mb-3 flex items-center">
                        <FaHeadset className="h-4 w-4 mr-2" />
                        Support Module
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-400">Module:</span>
                          <p className="text-sm text-white">{supportTicket.supportModuleId?.name || 'Unknown'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Project:</span>
                          <p className="text-sm text-white">{supportTicket.projectId?.name || 'Unknown'}</p>
                        </div>
                        {supportTicket.category && (
                          <div>
                            <span className="text-xs text-gray-400">Category:</span>
                            <p className="text-sm text-white">{supportTicket.category}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>



                  {/* Timestamps */}
                  <div className="bg-gray-800 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-white mb-3 flex items-center">
                      <FaClock className="h-4 w-4 mr-2" />
                      Timeline
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Created:</span>
                        <p className="text-white">{formatDate(supportTicket.timestamps?.createdAt)}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Last Updated:</span>
                        <p className="text-white">
                          {formatDate(
                            supportTicket.timestamps?.updatedAt ||
                            supportTicket.timestamps?.lastResponseAt ||
                            supportTicket.timestamps?.createdAt
                          )}
                        </p>
                      </div>
                      {supportTicket.timestamps?.lastResponseAt && (
                        <div>
                          <span className="text-gray-400">Last Response:</span>
                          <p className="text-white">{formatDate(supportTicket.timestamps.lastResponseAt)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Close
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SupportTicketDetail;
